import type { PedidoDeReceta } from '@/db/repos/orders';

/**
 * Para quien es cada platillo del dia, leido de los PEDIDOS.
 *
 * POR QUE EXISTE, Y QUE REEMPLAZA
 *
 * Antes este bloque leia las NOTAS del menu y trataba cada nota como si fuera
 * una persona que pidio. Se escribio cuando todavia no habia pedidos con nombre y
 * lo unico disponible era texto libre.
 *
 * Con el modulo de pedidos ya existiendo, eso se volvio enganoso: la nota
 * descriptiva "Con ensalada y arroz." aparecia como un pedido de 4 porciones,
 * cuando en realidad habia 2 pedidos, de David y de Felipe. Un numero inventado
 * en la pantalla que se lee de reojo por la manana es peor que no mostrar nada.
 *
 * Ahora la fuente es la tabla de pedidos: nombre real y porciones reales.
 *
 * NO se muestra si nadie ha pedido nada del dia: las tarjetas de cada platillo ya
 * lo dicen, y un bloque vacio seria ruido.
 */

export interface RenglonParaQuien {
  nombreReceta: string;
  /** Lo que el menu estima. Es referencia, no un plan de produccion. */
  estimado: number;
  pedido: PedidoDeReceta | undefined;
}

interface Props {
  renglones: readonly RenglonParaQuien[];
  titulo?: string;
}

export function ParaQuien({ renglones, titulo = 'Para quién es' }: Props) {
  const conPedidos = renglones.filter((r) => r.pedido !== undefined && r.pedido.cuantos > 0);
  if (conPedidos.length === 0) return null;

  const porciones = conPedidos.reduce((s, r) => s + (r.pedido?.porciones ?? 0), 0);

  return (
    <div className="rounded-panel border border-borde bg-superficie-suave p-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">{titulo}</p>
        <p className="shrink-0 text-xs text-texto-tenue">
          <span className="tabular-nums">{porciones}</span>{' '}
          {porciones === 1 ? 'porción en total' : 'porciones en total'}
        </p>
      </div>

      <ul className="mt-2 space-y-2">
        {renglones.map((renglon, indice) => (
          // Dos platillos del mismo nombre pueden caer el mismo dia, asi que el
          // indice forma parte de la clave.
          <li key={`${renglon.nombreReceta}-${indice}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span
                className={`text-sm font-semibold ${
                  renglon.pedido === undefined || renglon.pedido.cuantos === 0
                    ? 'text-texto-tenue'
                    : 'text-texto'
                }`}
              >
                {renglon.nombreReceta}
              </span>
              <span className="shrink-0 text-sm tabular-nums text-texto-suave">
                {renglon.pedido === undefined || renglon.pedido.cuantos === 0
                  ? 'nadie ha pedido'
                  : `${renglon.pedido.porciones} ${
                      renglon.pedido.porciones === 1 ? 'porción' : 'porciones'
                    }`}
              </span>
            </div>

            {renglon.pedido !== undefined && renglon.pedido.cuantos > 0 && (
              <ul className="mt-1 space-y-0.5">
                {renglon.pedido.clientes.map((cliente, i) => (
                  <li
                    key={`${cliente.clienteNombre}-${i}`}
                    className="flex items-baseline justify-between gap-3 text-sm text-texto-suave"
                  >
                    <span className="min-w-0 break-words">· {cliente.clienteNombre}</span>
                    <span className="shrink-0 tabular-nums">{cliente.porciones}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
